import React, { useState, useRef } from "react";
import PropTypes from "prop-types";
import { useShowAlert } from "../../../../context/AlertContext";
import { AddIcon, KeyIcon, XIcon } from "../../../../constants/Icons";

const AddNewPassword = ({ onClose, onAdd }) => {
  const [formData, setFormData] = useState({
    domain: "",
    username: "",
    password: "",
    notes: "",
  });
  const showAlert = useShowAlert();

  const [errors, setErrors] = useState({});

  // Refs for inputs
  const domainRef = useRef(null);
  const usernameRef = useRef(null);
  const passwordRef = useRef(null);
  const notesRef = useRef(null);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Clear error on change
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  // Individual field validation
  const validateField = (field, value) => {
    switch (field) {
      case "domain":
        if (!value) return "Domain is required.";
        break;
      case "username":
        if (!value) return "Username is required.";
        // if (!/[a-zA-Z0-9._]{1,100}/.test(value))
        //   return "Username must be 1-100 characters alphanumeric.";
        break;
      case "password":
        if (!value) return "Password is required.";
        // if (value.length < 1 || value.length > 100)
        //   return "Password must be 1-100 characters.";
        break;
      case "notes":
        if (value.length > 500) return "Notes cannot exceed 500 characters.";
        break;
      default:
        return "";
    }
    return "";
  };

  const handleSubmit = () => {
    const newErrors = {};
    Object.keys(formData).forEach((key) => {
      const error = validateField(key, formData[key]);
      if (error) newErrors[key] = error;
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      // Show first error as alert
      const firstError = newErrors[Object.keys(newErrors)[0]];
      showAlert("error", firstError);
      return;
    }

    onAdd(formData);
    onClose();
  };

  const handleKeyDown = (e, nextRef) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (nextRef && nextRef.current) {
        nextRef.current.focus();
      } else {
        handleSubmit();
      }
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-backdrop show"></div>
      <div
        className="modal show d-block"
        tabIndex="0"
        aria-modal="true"
        role="dialog"
      >
        <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
          <div className="modal-content">
            {/* Header */}
            <div className="modal-header">
              <h6 className="modal-title">New Password</h6>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
                aria-label="Close"
              ></button>
            </div>

            {/* Body */}
            <div className="modal-body">
              <div className="row align-items-center mb-4">
                <div className="col-auto">
                  <div
                    className="rounded-circle bg-secondary text-white d-flex align-items-center justify-content-center"
                    style={{
                      width: "70px",
                      height: "70px",
                      fontSize: "28px",
                      fontWeight: "bold",
                    }}
                  >
                    {formData.domain ? (
                      formData.domain[0].toUpperCase()
                    ) : (
                      <KeyIcon/>
                    )}
                  </div>
                </div>
                <div className="col">
                  <input
                    ref={domainRef}
                    type="text"
                    className={`form-control fw-bold`}
                    placeholder="Enter domain or title"
                    value={formData.domain}
                    onChange={(e) => handleChange("domain", e.target.value)}
                    onBlur={(e) =>
                      setErrors({
                        ...errors,
                        domain: validateField("domain", e.target.value),
                      })
                    }
                    onKeyDown={(e) => handleKeyDown(e, usernameRef)}
                  />
                  {errors.domain && (
                    <div className="error-message invalid-feedback">
                      {errors.domain}
                    </div>
                  )}
                </div>
              </div>

              <div className="px-2">
                <div className="mb-3">
                  <label className="fw-semibold mb-1">Username</label>
                  <input
                    ref={usernameRef}
                    type="text"
                    className={`form-control `}
                    placeholder="Enter username"
                    value={formData.username}
                    onChange={(e) => handleChange("username", e.target.value)}
                    onBlur={(e) =>
                      setErrors({
                        ...errors,
                        username: validateField("username", e.target.value),
                      })
                    }
                    onKeyDown={(e) => handleKeyDown(e, passwordRef)}
                  />
                  {errors.username && (
                    <div className="error-message invalid-feedback">
                      {errors.username}
                    </div>
                  )}
                </div>

                <div className="mb-3">
                  <label className="fw-semibold mb-1">Password</label>
                  <input
                    ref={passwordRef}
                    type="text"
                    className={`form-control `}
                    placeholder="Enter password"
                    value={formData.password}
                    onChange={(e) => handleChange("password", e.target.value)}
                    onBlur={(e) =>
                      setErrors({
                        ...errors,
                        password: validateField("password", e.target.value),
                      })
                    }
                    onKeyDown={(e) => handleKeyDown(e, notesRef)}
                  />
                  {errors.password && (
                    <div className="invalid-feedback">{errors.password}</div>
                  )}
                </div>

                <div className="mb-3">
                  <label className="fw-semibold mb-1">Notes</label>
                  <textarea
                    ref={notesRef}
                    className={`form-control`}
                    rows="4" // can adjust visible height
                    maxLength={500} // limit to 500 characters
                    placeholder="Any notes..."
                    value={formData.notes}
                    onChange={(e) => handleChange("notes", e.target.value)}
                    onBlur={(e) =>
                      setErrors({
                        ...errors,
                        notes: validateField("notes", e.target.value),
                      })
                    }
                    onKeyDown={(e) => handleKeyDown(e, null)}
                    style={{
                      whiteSpace: "pre-wrap", // wrap at spaces and preserve line breaks
                      wordWrap: "break-word", // break very long words if needed
                      overflowY: "auto", // vertical scroll if content exceeds rows
                      resize: "vertical", // allow user to resize vertically only
                      maxHeight: "150px",
                      // optional: stop expanding card too much
                    }}
                  />
                  {errors.notes && (
                    <div className="invalid-feedback">{errors.notes}</div>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="modal-footer d-block">
              <button
                type="button"
                className="btn btn-outline-secondary text-left"
                onClick={onClose}
              >
                <XIcon/> Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary text-right"
                onClick={handleSubmit}
              >
                <AddIcon/> Add
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

AddNewPassword.propTypes = {
  onClose: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
};

export default AddNewPassword;
